import { useEffect, useState } from 'react'
import { getPublicPlans } from '../services/promotions'
import { findPlanById } from '../helper-functions/planTier'

/**
 * Loads the self-service plan list (founding-grant plans filtered out) and
 * tracks the user's selection. Shared by the invitation + registration signup
 * flows. Pass `enabled=false` to defer the fetch until the picker is actually
 * shown (e.g. only for non-guest new invitees) — avoids a wasted round-trip on
 * the guest/existing-user paths that never render a picker.
 */
const usePublicPlans = (enabled = true): {
  plans: any[]
  plansLoading: boolean
  plansError: string
  selectedPlanId: string
  setSelectedPlanId: (id: string) => void
  selectedPlan: any
} => {
  const [plans, setPlans] = useState<any[]>([])
  const [plansLoading, setPlansLoading] = useState(true)
  const [plansError, setPlansError] = useState('')
  const [selectedPlanId, setSelectedPlanId] = useState('')

  useEffect(() => {
    if (!enabled) return
    let cancelled = false
    const run = async () => {
      setPlansLoading(true)
      try {
        const res = await getPublicPlans()
        const data = res.data?.data ?? res.data
        // Founding-conferring plans are grant-only — never shown in self-service lists.
        const activePlans = (Array.isArray(data) ? data : data?.plans ?? [])
          .filter((p: any) => !p?.grantsFoundingMember)
        if (cancelled) return
        setPlans(activePlans)
        if (activePlans.length > 0) setSelectedPlanId(activePlans[0].id)
      } catch (err: any) {
        if (!cancelled) setPlansError(err?.response?.data?.message ?? 'Failed to load plans.')
      } finally {
        if (!cancelled) setPlansLoading(false)
      }
    }
    run()
    return () => {
      cancelled = true
    }
  }, [enabled])

  const selectedPlan = findPlanById(plans, selectedPlanId)
  return { plans, plansLoading, plansError, selectedPlanId, setSelectedPlanId, selectedPlan }
}

export default usePublicPlans
